'use client'

import { type PolymorphicProps, useSlotProps } from '@mui/base'
import { type ComponentProps, type ElementType, type JSX } from 'react'

type CardContentOwnProps<RootComponentType extends ElementType = 'div'> = {
  slotProps?: {
    root?: ComponentProps<RootComponentType>
  }

  slots?: {
    root?: RootComponentType
  }
}

interface CardContentTypeMap<RootComponentType extends ElementType = 'div'> {
  defaultComponent: RootComponentType
  props: CardContentOwnProps<RootComponentType>
}

type CardContentProps<
  RootComponentType extends
    ElementType = CardContentTypeMap['defaultComponent'],
> = PolymorphicProps<CardContentTypeMap<RootComponentType>, RootComponentType>

/**
 * Cards contain content and actions about a single subject.
 */
export const CardContent = <RootComponentType extends ElementType = 'div'>(
  props: CardContentProps<RootComponentType>,
): JSX.Element => {
  const { slotProps, slots, ...restProps } = props

  const Root = slots?.root ?? 'div'
  const rootProps = useSlotProps({
    className: 'card-content',
    elementType: Root,
    externalForwardedProps: restProps,
    externalSlotProps: slotProps?.root,
    ownerState: undefined,
  })

  return <Root {...rootProps} />
}
